<template>
  <app-shell breadcrumbs="Home , Users">
    <div class="users__container">
      <div class="row no-gutters mt-4">
        <div class="col-12">
          <!-- Payment data table -->
          <div class="users__data-table-container">
            <data-table
              :actions="actions"
              :buttons="buttons"
              :column-configuration="columns"
              :filters="filters"
              :rows="users"
              @activate-user="activateUser"
              @create-user="createUser"
              @deactivate-user="promptToDeactivateUser"
            >
            </data-table>
          </div>
          <!-- End of the users data table -->
        </div>
      </div>
      <!-- Create a new user -->
      <modal
        action="Invite"
        :name="`create-user`"
        :no-icon="false"
        :title="`Invite a user to <b>${currentService.serviceName}</b>`"
        @cancel="cancelUserCreation"
        @save="saveUser(newUser)"
      >
        <div class="form-group">
          <label class="control-label" for="full_name">Name</label>
          <input type="text"
                 class="form-control"
                 name="full_name"
                 id="full_name"
                 v-model="newUser.fullName"
                 v-validate="'required|min:5'"
          >
          <span v-show="errors.has('full_name')" class="help is-danger">
            {{ errors.first('full_name') }}
          </span>
        </div>

        <div class="form-group">
          <label class="control-label" for="emailAddress">Email address</label>
          <input type="text"
                 class="form-control"
                 name="emailAddress"
                 id="emailAddress"
                 v-model="newUser.emailAddress"
                 v-validate="'required|email'">
          <span v-show="errors.has('emailAddress')" class="help is-danger">
            {{ errors.first('emailAddress') }}
          </span>
        </div>

        <div class="form-group">
          <label class="control-label" for="role">Role</label>
          <select id="role" name="role" class="form-control" v-model="newUser.roleCode" v-validate="'required'">
            <option v-for="(role, index) in roles" :value="role.roleCode" :key="`#${index}${role.name}`">{{ role.roleName }}</option>
          </select>
          <span v-show="errors.has('role')" class="help is-danger">
            {{ errors.first('role') }}
          </span>
        </div>
      </modal>
      <!-- End of create new user -->

      <!-- Deactivate an exisiting user prompt-->
      <modal
        action="Deactivate"
        :name="`deactivate-user`"
        :no-icon="false"
        :title="`<b>Are you sure?</b>`"
        @cancel="cancelUserDeactivation"
        @save="deactivateUser(currentUser)"
      >
        <p>
          Deactivating {{ currentUser.fullName }} will disable their account and as a result they will be
          unable to login to the checkout portal.
        </p>
      </modal>
      <!-- End deactivate an existing user prompt -->
    </div>
  </app-shell>
</template>

<script>
  import _ from 'lodash'
  import { mapActions, mapGetters } from 'vuex'
  import DataTable from '@/components/DataTable'
  import Modal from '@/components/Modal'
  import Shell from '@/components/Shell'
  import UserService from '@/api/services/user'

  const logInfo = !!(process.env.NODE_ENV !== 'production')

  export default {
    components: {
      'app-shell': Shell,
      'data-table': DataTable,
      'modal': Modal
    },
    computed: {
      ...mapGetters('services', ['currentService']),
      ...mapGetters('user', ['clientInfo', 'userInfo'])
    },
    data () {
      return {
        actions: [
          {
            name: 'Deactivate',
            event: 'deactivate-user',
            condition: {
              field: 'status',
              condition: 'isNot',
              value: 'Deactivated'
            }
          },
          {
            name: 'Activate',
            event: 'activate-user',
            condition: {
              field: 'status',
              condition: 'is',
              value: 'Deactivated'
            }
          }
        ],
        buttons: [
          {
            name: 'New',
            icon: 'add',
            event: 'create-user'
          }
        ],
        columns: {
          'emailAddress': {
            highlight: true
          },
          'fullName': {
            hide: true
          },
          'roleCode': {
            hide: true
          },
          'userActive': {
            hide: true
          },
          'userID': {
            hide: true
          }
        },
        currentUser: {},
        filters: [
          {
            label: 'Name',
            type: 'string',
            key: 'fullName'
          },
          {
            label: 'Amount',
            type: 'number',
            key: 'amount'
          },
          {
            label: 'Date',
            type: 'date',
            key: 'date'
          }
        ],
        newUser: {
          emailAddress: '',
          fullName: '',
          roleCode: ''
        },
        roles: [],
        users: []
      }
    },
    methods: {
      ...mapActions('auth', ['logout']),
      _logoutIfUserSelfDeactivated (user) {
        if (user.userID === this.userInfo.userID) {
          this.logout()
          this.$router.push('/login')
          return true
        }
        return false
      },
      activateUser (user) {
        logInfo && console.log('Activating the user =>', user)
        // Set the default parameters for the user. TODO: Find a better way to do this
        user.clientCode = this.clientInfo.client_code
        UserService.activateUser({ user })
          .then(response => {
            logInfo && console.log('Successfully deactivated user =>', { user, response })
            this.fetchClientUsers({ clientCode: this.clientInfo.client_code })
              .then(() => {
                this.$snackbar.open({
                  message: `${user.fullName} has been activated`,
                  type: 'is-success',
                  position: 'is-bottom',
                  queue: false
                })
              })
              .catch(() => {
                this.$snackbar.open({
                  message: `Failed to refresh the list`,
                  type: 'is-warning',
                  position: 'is-bottom',
                  queue: false,
                  actionText: 'RETRY',
                  onAction: () => {
                    this.fetchClientUsers({ clientCode: this.clientInfo.client_code })
                  }
                })
              })
          })
          .catch(reason => {
            logInfo && console.log('Failed to deactivate the user', { reason, user })
            this.$snackbar.open({
              message: `${user.fullName}'s deactivation failed`,
              type: 'is-error',
              position: 'is-bottom',
              queue: false,
              actionText: 'RETRY',
              onAction: () => {
                this.activateUser(user)
              }
            })
          })
      },
      cancelUserCreation () {
        this.$modal.hide('create-user')
        // Reset the user form
        this.newUser = {
          emailAddress: '',
          name: '',
          role: ''
        }
      },
      cancelUserDeactivation () {
        this.$modal.hide('deactivate-user')
        // Reset the current user
        this.currentUser = {}
      },
      createUser (user = {emailAddress: '', fullName: '', roleCode: ''}) {
        this.newUser = user
        this.$modal.show('create-user')
      },
      deactivateUser (user) {
        this.$modal.hide('deactivate-user')
        logInfo && console.log('Deactivating the user =>', user)
        // Set the default parameters for the user. TODO: Find a better way to do this
        user.clientCode = this.clientInfo.client_code
        user.userActive = 0
        UserService.deactivateUser({ user })
          .then(response => {
            logInfo && console.log('Successfully deactivated user =>', { user, response })
            if (!this._logoutIfUserSelfDeactivated(user)) {
              this.currentUser = {}
              this.fetchClientUsers({ clientCode: this.clientInfo.client_code })
                .then(() => {
                  this.$snackbar.open({
                    message: `${user.fullName} has been deactivated`,
                    type: 'is-success',
                    position: 'is-bottom',
                    queue: false
                  })
                })
                .catch(() => {
                  this.$snackbar.open({
                    message: `Failed to refresh the list`,
                    type: 'is-warning',
                    position: 'is-bottom',
                    queue: false,
                    actionText: 'RETRY',
                    onAction: () => {
                      this.fetchClientUsers({ clientCode: this.clientInfo.client_code })
                    }
                  })
                })
            }
          })
          .catch(reason => {
            logInfo && console.log('Failed to deactivate the user', { reason, user })
            this.$snackbar.open({
              message: `${user.fullName}'s deactivation failed`,
              type: 'is-error',
              position: 'is-bottom',
              queue: false,
              actionText: 'RETRY',
              onAction: () => {
                this.deactivateUser(user)
              }
            })
            this.currentUser = {}
          })
      },
      fetchClientUsers ({ clientCode }) {
        return new Promise((resolve, reject) => {
          UserService.fetchClientUsers({ clientCode: clientCode })
            .then(response => {
              logInfo && console.log('Successfully fetched this client\'s users', response)
              this.roles = response.roles
              this.users = response.users
              resolve({ roles: this.roles, users: this.users })
            })
            .catch(reason => {
              logInfo && console.log('Failed to fetch this client\'s users', reason)
              reject(reason)
            })
        })
      },
      promptToDeactivateUser (user) {
        this.currentUser = user
        this.$modal.show('deactivate-user')
      },
      saveUser (user) {
        this.$validator.validateAll()
          .then(result => {
            logInfo && console.log('Did the user information pass validation?', result)
            // TODO: Find a better way to attach the default user information
            user.mobileNumber = this.userInfo.MSISDN
            user.clientCode = this.clientInfo.client_code
            if (result) {
              this.cancelUserCreation()
              UserService.inviteUser({ user })
                .then(response => {
                  console.log(user.roleCode);
                  logInfo && console.log('Successfully invited a user', response)
                  this.$snackbar.open({
                    message: `${user.fullName} has been invited`,
                    type: 'is-success',
                    position: 'is-bottom',
                    queue: false
                  })
                  this.users.push({
                    name: user.fullName,
                    emailAddress: user.emailAddress,
                    role: (_.capitalize(UserService._retrieveRoleUsingCode(user.roleCode, this.roles).roleName)),
                    status: 'Invited',
                    date: '----/--/-- --:--:--'
                  })
                })
                .catch(reason => {
                  logInfo && console.log('Failed to invite a user', reason)
                  if (reason.STATCODE === 7) {
                    this.$snackbar.open({
                      message: `${user.fullName}'s invite failed`,
                      type: 'is-error',
                      position: 'is-bottom',
                      queue: false,
                      actionText: 'RETRY',
                      onAction: () => {
                        this.createUser(user)
                      }
                    })
                  } else {
                    this.$snackbar.open({
                      message: _.capitalize(reason.REASON),
                      type: 'is-warning',
                      position: 'is-bottom',
                      queue: false,
                      actionText: 'OK'
                    })
                  }
                })
            }
          })
          .catch(reason => {
            logInfo && console.log('Failed to validate the user information during invitation', reason)
          })
      }
    },
    mounted () {
      this.fetchClientUsers({ clientCode: this.clientInfo.client_code })
    },
    name: 'users'
}
</script>



// WEBPACK FOOTER //
// src/views/Users.vue